import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X, ChevronRight, ChevronLeft } from "lucide-react";
import { WafaLogo } from "@/components/WafaLogo";
import { NAV } from "@/data/navigation";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { useWhatsAppLink, WA_LABEL } from "@/lib/whatsapp";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

const NAV_KEY = { "who-we-serve": "whoWeServe", services: "services", solutions: "solutions", meyar: "meyar", tools: "tools", work: "work", company: "company" };
const navLabel = (t, item) => t.nav?.[NAV_KEY[item.id]] || item.label;

// ---------- Desktop mega-panel ----------

function ColumnHeading({ text }) {
  return (
    <div className="mb-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-wafa-primary">
      {text}
    </div>
  );
}

function PanelLink({ item, onNavigate }) {
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      data-testid={`mega-link-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="group -mx-3 block rounded-md px-3 py-2 transition-colors hover:bg-wafa-cream/50"
    >
      <div className="font-medium text-wafa-deep group-hover:text-wafa-primary">
        {item.title}
      </div>
      {item.desc ? (
        <div className="mt-0.5 text-xs text-wafa-ink/60">{item.desc}</div>
      ) : null}
    </Link>
  );
}

function ViewAllLink({ viewAll, onNavigate }) {
  if (!viewAll) return null;
  return (
    <div className="mt-8 flex items-center justify-end border-t border-wafa-deep/10 pt-5">
      <Link
        to={viewAll.to}
        onClick={onNavigate}
        data-testid={`mega-view-all-${viewAll.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="inline-flex items-center gap-1 text-sm font-semibold text-wafa-primary transition-transform hover:translate-x-0.5"
      >
        {viewAll.label}
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function PromoTile({ promo, onNavigate }) {
  if (!promo) return null;
  // The "team" variant renders on cream with dark type; the default variant
  // renders on wafa-deep with light type. Named for the visual, not the copy.
  const isCreamTile = promo.variant === "team";
  return (
    <div
      data-testid="mega-promo"
      className={cn(
        "flex flex-col justify-between rounded-lg p-6",
        isCreamTile ? "bg-wafa-cream/70" : "bg-wafa-deep text-white",
      )}
    >
      <div>
        <div className={cn("font-heading text-lg font-semibold", isCreamTile ? "text-wafa-deep" : "text-white")}>
          {promo.title}
        </div>
        <div className={cn("mt-2 text-sm", isCreamTile ? "text-wafa-ink/70" : "text-white/80")}>
          {promo.line}
        </div>
      </div>
      <Link
        to={promo.cta.to}
        onClick={onNavigate}
        data-testid="mega-promo-cta"
        className={cn(
          "mt-6 inline-flex items-center gap-1.5 self-start rounded-md px-4 py-2 text-sm font-semibold transition-colors",
          isCreamTile
            ? "bg-wafa-deep text-white hover:bg-wafa-primary"
            : "bg-wafa-primary text-white hover:bg-wafa-sunshine hover:text-wafa-deep",
        )}
      >
        {promo.cta.label} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function MegaPanel({ panel, onNavigate }) {
  if (!panel) return null;

  if (panel.kind === "columns-with-promo") {
    return (
      <div className="grid gap-10 md:grid-cols-3">
        <div className="md:col-span-2">
          <div className="grid grid-cols-1 gap-x-8 gap-y-1 sm:grid-cols-2">
            {panel.columns.map((col, ci) => (
              <div key={ci}>
                {col.heading ? <ColumnHeading text={col.heading} /> : null}
                <div className="flex flex-col gap-1">
                  {col.items.map((it) => (
                    <PanelLink key={it.title} item={it} onNavigate={onNavigate} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <ViewAllLink viewAll={panel.viewAll} onNavigate={onNavigate} />
        </div>
        <PromoTile promo={panel.promo} onNavigate={onNavigate} />
      </div>
    );
  }

  const cols = panel.columns.length;
  return (
    <div>
      <div
        className={cn(
          "grid gap-x-10 gap-y-8",
          cols === 4 ? "md:grid-cols-4" : cols === 3 ? "md:grid-cols-3" : "md:grid-cols-2",
        )}
      >
        {panel.columns.map((col, ci) => (
          <div key={ci}>
            {col.heading ? <ColumnHeading text={col.heading} /> : null}
            <div className="flex flex-col gap-1">
              {col.items.map((it) => (
                <PanelLink key={it.title} item={it} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <ViewAllLink viewAll={panel.viewAll} onNavigate={onNavigate} />
    </div>
  );
}

// ---------- Mobile drawer ----------

function MobileDrawer({ open, onClose }) {
  const [subPanelId, setSubPanelId] = useState(null);
  const { t, lang } = useI18n();
  const location = useLocation();
  const wa = useWhatsAppLink("mobile_menu");

  // Reset sub-panel when drawer closes / route changes.
  useEffect(() => {
    if (!open) setSubPanelId(null);
  }, [open]);
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const subPanel = NAV.find((n) => n.id === subPanelId);

  if (!open) return null;

  // NOTE: rendered via a React portal into document.body. The Header itself
  // uses `backdrop-blur-md`, and any element with a `backdrop-filter` (or
  // `filter`, `transform`, `will-change`, `contain: paint`) becomes the
  // containing block for its `position: fixed` descendants — the drawer
  // was being clipped to the header's ~60px sticky box. Portalling escapes
  // that stacking context so `fixed inset-0` snaps to the viewport again.
  return createPortal(
    <div
      data-testid="mobile-nav"
      className="fixed inset-0 z-[60] flex flex-col bg-white lg:hidden"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex items-center justify-between border-b border-wafa-deep/10 px-4 py-3">
        <Link to="/" onClick={onClose}>
          <WafaLogo />
        </Link>
        <button
          data-testid="mobile-nav-close"
          onClick={onClose}
          aria-label="Close menu"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-wafa-deep/15 text-wafa-deep"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <div
          className="absolute inset-0 flex transition-transform duration-300 ease-out"
          style={{ transform: subPanelId ? "translateX(-100%)" : "translateX(0)" }}
        >
          {/* Level 1 — top-level items */}
          <nav className="w-full shrink-0 overflow-y-auto px-4 py-4">
            <ul>
              {NAV.map((item) => (
                <li key={item.id}>
                  {item.panel ? (
                    <button
                      onClick={() => setSubPanelId(item.id)}
                      data-testid={`mobile-nav-open-${item.id}`}
                      className="flex w-full items-center justify-between py-4 text-start text-base font-medium text-wafa-deep"
                    >
                      <span className="flex items-center gap-2">
                        {navLabel(t, item)}
                        {item.isNew ? (
                          <span className="rounded-full bg-wafa-carrot px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                            New
                          </span>
                        ) : null}
                      </span>
                      <ChevronRight className="h-4 w-4 text-wafa-deep/60" />
                    </button>
                  ) : (
                    <Link
                      to={item.to}
                      onClick={onClose}
                      data-testid={`mobile-nav-${item.id}`}
                      className="flex w-full items-center justify-between py-4 text-base font-medium text-wafa-deep"
                    >
                      {navLabel(t, item)}
                      <ChevronRight className="h-4 w-4 text-wafa-deep/60" />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              onClick={onClose}
              className="mt-6 flex items-center justify-center rounded-md bg-wafa-primary px-4 py-3 text-sm font-semibold text-white"
              data-testid="mobile-nav-cta"
            >
              {t.nav.getQuote}
            </Link>
            {wa ? (
              <a
                href={wa.href}
                onClick={wa.onClick}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={WA_LABEL[lang]}
                data-testid="mobile-nav-whatsapp"
                className="mt-3 flex items-center justify-center gap-2 rounded-md bg-[#25D366] px-4 py-3 text-sm font-semibold text-white"
              >
                <WhatsAppIcon size={18} fill="#fff" />
                {WA_LABEL[lang]}
              </a>
            ) : null}
          </nav>

          {/* Level 2 — sub-panel */}
          <div className="w-full shrink-0 overflow-y-auto px-4 py-4">
            {subPanel ? (
              <>
                <button
                  onClick={() => setSubPanelId(null)}
                  data-testid="mobile-nav-back"
                  className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-wafa-deep"
                >
                  <ChevronLeft className="h-4 w-4" /> Back to menu
                </button>
                <div className="mb-4 font-heading text-2xl font-semibold text-wafa-deep">
                  {navLabel(t, subPanel)}
                </div>
                {subPanel.panel?.columns?.map((col, ci) => (
                  <div key={ci} className="mb-6">
                    {col.heading ? <ColumnHeading text={col.heading} /> : null}
                    <div className="flex flex-col">
                      {col.items.map((it) => (
                        <Link
                          key={it.title}
                          to={it.to}
                          onClick={onClose}
                          data-testid={`mobile-sub-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="border-b border-wafa-deep/10 py-3 text-sm text-wafa-deep"
                        >
                          <div className="font-medium">{it.title}</div>
                          {it.desc ? <div className="mt-0.5 text-xs text-wafa-ink/60">{it.desc}</div> : null}
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
                {subPanel.panel?.promo ? (
                  <div className="mt-2">
                    <PromoTile promo={subPanel.panel.promo} onNavigate={onClose} />
                  </div>
                ) : null}
                {subPanel.panel?.viewAll ? (
                  <Link
                    to={subPanel.panel.viewAll.to}
                    onClick={onClose}
                    className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-wafa-primary"
                  >
                    {subPanel.panel.viewAll.label} <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------- Header ----------

export function Header() {
  const { t, lang, toggleLang } = useI18n();
  const [openId, setOpenId] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const rootRef = useRef(null);
  const openTimer = useRef();
  const closeTimer = useRef();
  const location = useLocation();
  const wa = useWhatsAppLink("header");

  const clearTimers = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  useEffect(() => () => clearTimers(), []);

  // Close on route change.
  useEffect(() => {
    setOpenId(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenId(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpenId(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const openWithDelay = useCallback((id) => {
    clearTimers();
    openTimer.current = setTimeout(() => setOpenId(id), 80);
  }, []);
  const closeWithDelay = useCallback(() => {
    clearTimers();
    closeTimer.current = setTimeout(() => setOpenId(null), 150);
  }, []);
  const holdOpen = useCallback((id) => {
    clearTimers();
    setOpenId(id);
  }, []);

  const activePanel = NAV.find((n) => n.id === openId)?.panel;

  return (
    <header
      ref={rootRef}
      data-testid="site-header"
      className="sticky top-0 z-40 border-b border-wafa-deep/10 bg-white/95 backdrop-blur-md"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 lg:px-6">
        <Link to="/" data-testid="header-home-link" className="shrink-0">
          <WafaLogo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
          {NAV.map((item) => {
            const hasPanel = !!item.panel;
            const isOpen = openId === item.id;
            const shared = "relative whitespace-nowrap px-2 py-2 text-sm font-medium transition-colors xl:px-3";
            const cls = cn(
              shared,
              isOpen ? "text-wafa-primary" : "text-wafa-ink/85 hover:text-wafa-deep",
            );

            if (!hasPanel) {
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  data-testid={`nav-${item.id}`}
                  className={cls}
                  onMouseEnter={closeWithDelay}
                >
                  {navLabel(t, item)}
                </Link>
              );
            }
            return (
              <button
                key={item.id}
                type="button"
                data-testid={`nav-${item.id}`}
                aria-haspopup="true"
                aria-expanded={isOpen}
                className={cls}
                onMouseEnter={() => openWithDelay(item.id)}
                onMouseLeave={closeWithDelay}
                onFocus={() => holdOpen(item.id)}
                onClick={() => setOpenId(isOpen ? null : item.id)}
              >
                <span className="inline-flex items-center gap-1.5">
                  {navLabel(t, item)}
                  {item.isNew ? (
                    <span className="rounded-full bg-wafa-carrot px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                      New
                    </span>
                  ) : null}
                </span>
                {isOpen ? (
                  <span className="absolute inset-x-3 -bottom-[13px] h-[2px] bg-wafa-primary" aria-hidden="true" />
                ) : null}
              </button>
            );
          })}
        </nav>

        <div className="flex shrink-0 items-center gap-2 lg:gap-3">
          <a
            href={wa?.href || "#"}
            onClick={wa?.onClick}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp"
            aria-label={WA_LABEL[lang]}
            className="inline-flex h-9 min-w-9 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#25D366] px-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#1ebe5a] xl:px-4"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="#fff" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.031-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.148-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347M12.03 3.6c-4.647 0-8.407 3.76-8.407 8.407 0 1.594.437 3.14 1.271 4.482L3.5 20.5l4.116-1.34a8.362 8.362 0 004.414 1.25h.004c4.646 0 8.406-3.76 8.406-8.407 0-2.246-.874-4.359-2.462-5.947A8.352 8.352 0 0012.03 3.6"/>
            </svg>
            <span className="hidden xl:inline" data-testid="header-whatsapp-label">{WA_LABEL[lang]}</span>
          </a>

          <Link
            to="/contact"
            data-testid="header-cta"
            className="hidden whitespace-nowrap rounded-md bg-wafa-primary px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-wafa-deep md:inline-flex"
          >
            {t.nav.getQuote}
          </Link>

          <button
            data-testid="header-lang-toggle"
            onClick={toggleLang}
            aria-label="Toggle language"
            className="rounded-md border border-wafa-deep/15 px-2.5 py-1.5 text-xs font-semibold text-wafa-deep transition-colors hover:border-wafa-deep hover:bg-wafa-cream/60"
          >
            {lang === "en" ? (
              <span>
                <span className="text-wafa-deep">EN</span>
                <span className="mx-1 text-wafa-deep/30">|</span>
                <span className="text-wafa-deep/50">ع</span>
              </span>
            ) : (
              <span>
                <span className="text-wafa-deep/50">EN</span>
                <span className="mx-1 text-wafa-deep/30">|</span>
                <span className="text-wafa-deep">ع</span>
              </span>
            )}
          </button>

          <button
            data-testid="header-mobile-toggle"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-wafa-deep/15 lg:hidden"
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Full-width mega-panel — desktop only. */}
      {activePanel ? (
        <div
          data-testid={`mega-panel-${openId}`}
          onMouseEnter={() => holdOpen(openId)}
          onMouseLeave={closeWithDelay}
          className="absolute inset-x-0 top-full hidden border-t border-wafa-deep/10 bg-white shadow-[0_20px_40px_-20px_rgba(14,79,58,0.25)] lg:block"
        >
          <div className="mx-auto max-w-7xl px-4 py-8 lg:px-6 lg:py-10">
            <MegaPanel panel={activePanel} onNavigate={() => setOpenId(null)} />
          </div>
        </div>
      ) : null}

      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}
